import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../api/auth.api';
import LoadingScreen from '../../components/ui/LoadingScreen';
import Spinner from '../../components/ui/Spinner';
import { Lock, ArrowLeft, ArrowRight, Check, Eye, EyeOff, ShieldCheck, Icon } from '../../components/ui/Icon';

const MIN_PASSWORD_LENGTH = 8;

type Status = 'checking' | 'form' | 'invalid' | 'done';

function apiError(err: unknown): string {
  return (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? '';
}

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [status, setStatus]       = useState<Status>('checking');
  const [needs2FA, setNeeds2FA]   = useState(false);
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [otpCode, setOtpCode]     = useState('');
  const [showPassword, setShow]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    authApi.checkResetToken(token)
      .then(({ twoFactorRequired }) => { setNeeds2FA(twoFactorRequired); setStatus('form'); })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= MIN_PASSWORD_LENGTH && password === confirm && (!needs2FA || otpCode.length === 6);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.resetPassword({ token, password, ...(needs2FA ? { twoFactorCode: otpCode } : {}) });
      setStatus('done');
    } catch (err: unknown) {
      const code = apiError(err);
      if (code === 'TWO_FACTOR_REQUIRED') {
        // Pre-flight said otherwise (2FA switched on between the two calls) —
        // reveal the field instead of dead-ending on an opaque error.
        setNeeds2FA(true);
        setError(t('auth.resetPassword.twoFactorNeeded'));
      } else if (code === 'TWO_FACTOR_INVALID') {
        setError(t('auth.twoFactor.invalidCode'));
      } else if (code === 'RESET_TOKEN_INVALID') {
        setStatus('invalid');
      } else {
        setError(code || t('auth.resetPassword.failed'));
      }
    } finally {
      setLoading(false);
    }
  }

  if (status === 'checking') return <LoadingScreen fullscreen label={t('auth.resetPassword.checking')} />;

  if (status === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f0' }}>
        <div className="w-full max-w-md px-8">
          <div className="w-14 h-14 mb-6 rounded-2xl flex items-center justify-center bg-slate-100">
            <span className="text-2xl">✕</span>
          </div>
          <h1 className="font-serif text-[42px] font-light leading-none tracking-tight mb-4" style={{ color: '#0b1420', letterSpacing: -1.6 }}>
            {t('auth.resetPassword.invalidTitle')}
          </h1>
          <p className="font-serif font-medium text-base leading-relaxed mb-8" style={{ color: '#3a4558' }}>
            {t('auth.resetPassword.invalidBody')}
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-3 font-sans font-medium text-base px-6 py-3 rounded-xl no-underline"
            style={{ background: '#0b1420', color: '#faf7f0' }}
          >
            {t('auth.resetPassword.requestNew')}
            <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#d9b382', color: '#0b1420' }}>
              <Icon as={ArrowRight} size={12} />
            </span>
          </Link>
        </div>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f0' }}>
        <div className="w-full max-w-md px-8">
          <div className="w-14 h-14 mb-6 rounded-2xl flex items-center justify-center bg-teal-100">
            <Icon as={Check} size={26} className="text-teal-600" />
          </div>
          <h1 className="font-serif text-[42px] font-light leading-none tracking-tight mb-4" style={{ color: '#0b1420', letterSpacing: -1.6 }}>
            {t('auth.resetPassword.doneTitle')}
          </h1>
          <p className="font-serif font-medium text-base leading-relaxed mb-8" style={{ color: '#3a4558' }}>
            {t('auth.resetPassword.doneBody')}
          </p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="inline-flex items-center gap-3 font-sans font-medium text-base px-6 py-3 rounded-xl"
            style={{ background: '#0b1420', color: '#faf7f0', border: 'none', cursor: 'pointer' }}
          >
            {t('auth.resetPassword.goToLogin')}
            <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#d9b382', color: '#0b1420' }}>
              <Icon as={ArrowRight} size={12} />
            </span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f0' }}>
      <div className="w-full max-w-md px-8">
        <p className="font-mono text-[10px] tracking-[2px] uppercase mb-3" style={{ color: '#8a5a2b' }}>
          {t('auth.resetPassword.eyebrow')}
        </p>
        <h1 className="font-serif text-[42px] font-light leading-none tracking-tight mb-4" style={{ color: '#0b1420', letterSpacing: -1.6 }}>
          {t('auth.resetPassword.title')}
        </h1>
        <p className="font-serif font-medium text-base leading-relaxed mb-8" style={{ color: '#3a4558' }}>
          {t('auth.resetPassword.subtitle')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* New password */}
          <div>
            <label htmlFor="new-pw" className="block font-mono text-[10px] tracking-[1.6px] uppercase mb-2" style={{ color: '#6b7689' }}>
              {t('auth.resetPassword.newPassword')}
            </label>
            <div className="flex items-center gap-2.5" style={{ border: `1px solid ${tooShort ? '#dc2626' : '#d6dae2'}`, borderRadius: 10, height: 54, background: '#fff', padding: '0 14px' }}>
              <Icon as={Lock} size={16} style={{ color: '#6b7689', flexShrink: 0 }} />
              <input
                id="new-pw" type={showPassword ? 'text' : 'password'}
                className="flex-1 outline-none bg-transparent text-sm"
                style={{ fontFamily: 'Inter, sans-serif', color: '#0b1420', height: '100%', border: 'none' }}
                placeholder={t('auth.login.passwordPlaceholder')}
                value={password} onChange={e => setPassword(e.target.value)}
                required minLength={MIN_PASSWORD_LENGTH} autoFocus autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShow(s => !s)}
                className="shrink-0 p-1.5 rounded hover:bg-slate-100 transition-colors cursor-pointer"
                style={{ color: '#6b7689', background: 'none', border: 'none' }}
                aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                tabIndex={-1}
              >
                <Icon as={showPassword ? EyeOff : Eye} size={16} />
              </button>
            </div>
            {tooShort && (
              <p className="text-[11px] mt-1.5" style={{ color: '#dc2626' }}>
                {t('auth.resetPassword.tooShort', { min: MIN_PASSWORD_LENGTH })}
              </p>
            )}
          </div>

          {/* Confirm */}
          <div>
            <label htmlFor="confirm-pw" className="block font-mono text-[10px] tracking-[1.6px] uppercase mb-2" style={{ color: '#6b7689' }}>
              {t('auth.resetPassword.confirmPassword')}
            </label>
            <div className="flex items-center gap-2.5" style={{ border: `1px solid ${mismatch ? '#dc2626' : '#d6dae2'}`, borderRadius: 10, height: 54, background: '#fff', padding: '0 14px' }}>
              <Icon as={Lock} size={16} style={{ color: '#6b7689', flexShrink: 0 }} />
              <input
                id="confirm-pw" type={showPassword ? 'text' : 'password'}
                className="flex-1 outline-none bg-transparent text-sm"
                style={{ fontFamily: 'Inter, sans-serif', color: '#0b1420', height: '100%', border: 'none' }}
                placeholder={t('auth.resetPassword.confirmPlaceholder')}
                value={confirm} onChange={e => setConfirm(e.target.value)}
                required autoComplete="new-password"
              />
            </div>
            {mismatch && (
              <p className="text-[11px] mt-1.5" style={{ color: '#dc2626' }}>
                {t('auth.resetPassword.mismatch')}
              </p>
            )}
          </div>

          {/* 2FA — email access alone must not be enough to take the account over */}
          {needs2FA && (
            <div>
              <label htmlFor="otp" className="flex items-center gap-2 font-mono text-[10px] tracking-[1.6px] uppercase mb-2" style={{ color: '#6b7689' }}>
                <Icon as={ShieldCheck} size={13} />
                {t('auth.twoFactor.codeLabel')}
              </label>
              <input
                id="otp" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                className="w-full text-center tracking-widest font-mono outline-none"
                style={{ border: '1px solid #d6dae2', borderRadius: 10, height: 54, background: '#fff', color: '#0b1420', fontSize: 22 }}
                placeholder="000000"
                value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required autoComplete="one-time-code"
              />
              <p className="text-[11px] mt-1.5" style={{ color: '#6b7689' }}>
                {t('auth.resetPassword.twoFactorHint')}
              </p>
            </div>
          )}

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">⚠ {error}</p>}

          <button
            type="submit"
            disabled={loading || !canSubmit}
            className="w-full flex items-center justify-center gap-3 font-sans font-medium text-lg transition-all"
            style={{ marginTop: 10, height: 56, background: '#0b1420', color: '#faf7f0', borderRadius: 10, border: 'none', cursor: loading || !canSubmit ? 'not-allowed' : 'pointer', opacity: canSubmit ? 1 : 0.5 }}
          >
            {loading ? <Spinner size="sm" /> : (
              <>
                {t('auth.resetPassword.submit')}
                <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: '#d9b382', color: '#0b1420' }}>
                  <Icon as={ArrowRight} size={13} />
                </span>
              </>
            )}
          </button>
        </form>

        <Link
          to="/login"
          className="inline-flex items-center gap-2.5 text-sm font-medium no-underline mt-7"
          style={{ color: '#6b7689' }}
        >
          <Icon as={ArrowLeft} size={15} />
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </div>
    </div>
  );
}
